import React, { useState } from 'react';
import { Pagination } from '../../components/client';
import FilterBar from '../../components/shared/FilterBar';
import MarketingListingCard from '../../components/home/MarketingListingCard';
import MarketingBrowseEmpty from '../../components/home/MarketingBrowseEmpty';
import MarketingBrowseShell, {
  MarketingBrowseGrid,
  MarketingBrowseSkeleton,
} from '../../components/home/MarketingBrowseShell';
import { useBrowseVendors } from '../../hooks/useBrowseVendors';
import { pageText } from '../../content/publicPages';
import { usePublicPage } from '../../hooks/usePublicPage';
import VendorPerformanceBadge from '../../components/client/VendorPerformanceBadge';

const BrowseVendors = () => {
  const { saved, defaults } = usePublicPage('browse');
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const {
    searchTerm,
    setSearchTerm,
    location,
    setLocation,
    priceMin,
    setPriceMin,
    priceMax,
    setPriceMax,
    selectedPriceRange,
    setSelectedPriceRange,
    selectedRating,
    setSelectedRating,
    sortBy,
    setSortBy,
    setCurrentPage,
    vendors: sortedVendors,
    pagination,
    loading,
    clearAllFilters,
  } = useBrowseVendors();

  const sortOptions = [
    { value: 'none', label: 'Recommended' },
    { value: 'performance', label: 'Best Match' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'reviews', label: 'Most Reviews' },
    { value: 'distance', label: 'Nearest' },
  ];

  const serviceMeta = (vendor) =>
    `${vendor.serviceCount} ${vendor.serviceCount === 1 ? 'service' : 'services'}`;

  const countLabel = loading
    ? 'Loading…'
    : `${sortedVendors.length} vendor${sortedVendors.length === 1 ? '' : 's'}`;

  const filterSlot = (
    <FilterBar
      selectedPriceRange={selectedPriceRange}
      setSelectedPriceRange={setSelectedPriceRange}
      selectedRating={selectedRating}
      setSelectedRating={setSelectedRating}
      showMoreFilters={showMoreFilters}
      setShowMoreFilters={setShowMoreFilters}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      location={location}
      setLocation={setLocation}
      selectedCategory=""
      setSelectedCategory={() => {}}
      categories={[]}
      sortBy={sortBy}
      setSortBy={setSortBy}
      sortOptions={sortOptions}
      clearAllFilters={clearAllFilters}
      showCategoryFilter={false}
      priceMin={priceMin}
      setPriceMin={setPriceMin}
      priceMax={priceMax}
      setPriceMax={setPriceMax}
    />
  );

  return (
    <MarketingBrowseShell
      mobileTitle={pageText(saved.title, defaults.title)}
      title={pageText(saved.title, defaults.title)}
      countLabel={countLabel}
      locationLabel={location?.trim() || null}
      filterSlot={filterSlot}
    >
      {loading ? (
        <MarketingBrowseSkeleton />
      ) : sortedVendors.length > 0 ? (
        <>
          <MarketingBrowseGrid>
            {sortedVendors.map((vendor) => (
              <MarketingListingCard
                key={vendor.id}
                href={`/vendor/${vendor.id}`}
                image={vendor.image}
                title={vendor.name}
                subtitle={vendor.category}
                location={vendor.location || null}
                rating={vendor.rating}
                meta={serviceMeta(vendor)}
                reviewCount={vendor.reviewCount}
                featured={vendor.featured}
                imageOverlay={
                  <VendorPerformanceBadge
                    score={vendor.performanceScore}
                    bookingsCount={vendor.platformBookingsCount}
                    className="border-white/70 bg-white/95 shadow-sm"
                  />
                }
              />
            ))}
          </MarketingBrowseGrid>
          <Pagination
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            perPage={pagination.per_page}
            total={pagination.total}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <MarketingBrowseEmpty
          title={pageText(saved.empty_title, defaults.empty_title)}
          description={pageText(saved.empty_body, defaults.empty_body)}
          onClear={clearAllFilters}
          secondaryHref="/"
          secondaryLabel="Back to home"
        />
      )}
    </MarketingBrowseShell>
  );
};

export default BrowseVendors;
